<script>
import { useField } from 'vee-validate';
import { toRef, computed } from 'vue';
import { modes } from '@/components/forms/helpers/interactionModes';

import InputLabel from '@/components/forms/helpers/InputLabel.vue';
import InputError from '@/components/forms/helpers/InputError.vue';
import InputText from 'primevue/inputtext';

export default {
  name: 'InputTextGroup',
  components: {
    InputLabel,
    InputError,
    InputText,
  },
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: null,
    },
    tooltipIcon: {
      type: String,
      default: 'pi-info-circle',
    },
    required: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    mode: {
      type: String,
      default: 'lazy',
    },
  },
  setup(props) {
    // https://stackblitz.com/edit/vee-validate-v4-dynamic-validation-triggers-iwuvpm?file=src%2Fcomponents%2FCustomField.vue
    const { meta, value, errorMessage, handleChange, handleBlur } = useField(
      toRef(props, 'name'),
      null,
      {
        validateOnValueUpdate: false,
      },
    );

    const handlers = computed(() => {
      const on = {
        blur: handleBlur,
        input: [(e) => handleChange(e, false)],
      };

      const triggers = modes[props.mode]({
        errorMessage,
        meta,
      });

      triggers.forEach((t) => {
        if (Array.isArray(on[t])) {
          on[t].push(handleChange);
        } else {
          on[t] = handleChange;
        }
      });

      return on;
    });

    return {
      meta,
      value,
      errorMessage,
      handlers,
    };
  },
};
</script>

<template>
  <div>
    <InputLabel
      v-if="label"
      :name="name"
      :label="label"
      :tooltip="tooltip"
      :tooltip-icon="tooltipIcon"
      :required="required"
    >
      <slot name="label"></slot>
    </InputLabel>
    <div class="flex items-center">
      <InputText
        :value="value"
        :id="name"
        :aria-describedby="`${name}-help`"
        class="w-full"
        :class="{ 'p-invalid': errorMessage }"
        :type="type"
        v-bind="$attrs"
        v-on="handlers"
      />
      <slot name="input"></slot>
    </div>
    <InputError :name="name" :error-message="errorMessage" />
  </div>
</template>
